import { StrictMode } from "react";
import { createRoot, type Root } from "react-dom/client";
import {
  Decoration,
  EditorView,
  MatchDecorator,
  ViewPlugin,
  WidgetType,
  type DecorationSet,
  type ViewUpdate,
} from "@codemirror/view";
import { VariableChip } from "@/components/Form/ComplexMarkdownField/VariableChip";
import {
  TEMPLATE_VAR_REF_PATTERN,
  VAR_REF_PATTERN,
  normalizeVariableRefId,
  type VariableRefSyntax,
} from "@/components/SpotflowV2/Sidebar/variableRefs";

// Each widget's DOM hosts a live VariableChip fed by the shared inputsStore.
// Roots are keyed by DOM node, NOT widget instance: CodeMirror reuses
// instances across equal chips and may redraw a widget's DOM (toDOM +
// destroy(oldDom), in either order), so instance state would unmount the
// wrong root. Unmount is deferred — CodeMirror destroys widgets during view
// updates, when React forbids unmounting.
const chipRoots = new WeakMap<HTMLElement, Root>();

class VariableChipWidget extends WidgetType {
  constructor(
    private readonly editorId: string,
    private readonly refId: string
  ) {
    super();
  }

  eq(other: VariableChipWidget): boolean {
    return other.refId === this.refId && other.editorId === this.editorId;
  }

  toDOM(): HTMLElement {
    const span = document.createElement("span");
    span.className = "variable-chip";
    span.setAttribute("data-variable-id", this.refId);
    const root = createRoot(span);
    chipRoots.set(span, root);
    root.render(
      <StrictMode>
        <VariableChip editorId={this.editorId} inputId={this.refId} />
      </StrictMode>
    );
    return span;
  }

  destroy(dom: HTMLElement): void {
    const root = chipRoots.get(dom);
    if (!root) return;
    chipRoots.delete(dom);
    queueMicrotask(() => root.unmount());
  }
}

// Every `:var[...]` token renders as a display-only chip; the token text
// stays in the document verbatim, so the stored value is exactly what the
// user sees. Atomic ranges make caret movement and backspace treat each
// token as one unit.
export function variableChips(
  editorId: string,
  syntax: VariableRefSyntax = "var",
  knownIds?: ReadonlySet<string>
) {
  const matcher = new MatchDecorator({
    regexp: new RegExp(
      (syntax === "template" ? TEMPLATE_VAR_REF_PATTERN : VAR_REF_PATTERN)
        .source,
      "g"
    ),
    decoration: (match) => {
      const refId =
        syntax === "template" ? match[1] : normalizeVariableRefId(match[1]);
      // `((...))` is also plain SQL, so a template token is a chip only when
      // it names one of the node's variables.
      if (knownIds && !knownIds.has(refId)) return null;
      return Decoration.replace({
        widget: new VariableChipWidget(editorId, refId),
      });
    },
  });
  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = matcher.createDeco(view);
      }

      update(update: ViewUpdate) {
        this.decorations = matcher.updateDeco(update, this.decorations);
      }
    },
    {
      decorations: (v) => v.decorations,
      provide: (p) =>
        EditorView.atomicRanges.of(
          (view) => view.plugin(p)?.decorations ?? Decoration.none
        ),
    }
  );
}
